// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import produce from 'immer';
import { type ThunkDispatch } from '@reduxjs/toolkit';
import type * as BrokerageTypes from '@shared/data-access/types/brokerage-types';
import { getAssetsInfoBundle } from '../assets/get-assets-info';
import { type AssetInfo } from '../../interfaces/api/ai/assets';
import { type AccountsPositionsData } from '../../interfaces/api/brokerage/brokerage';
import { mergeClientHoldings } from './merge-client-holdings';
import { resolveHoldingDisplayAssetType } from '../../utils/holding-asset-type';

/**
 * Builds enriched account positions data from raw API response.
 *
 * Transforms raw account positions by merging duplicate holdings, fetching
 * additional asset metadata from the assets API, and normalizing asset types.
 *
 * @remarks
 * This function performs the following transformations:
 *
 * 1. **Extracts unique tickers/ISINs** from all holdings across accounts
 * 2. **Fetches asset info** from the assets API for enrichment (logo, sector, etc.)
 * 3. **Merges duplicate holdings** with the same ticker/ISIN and currency, aggregating
 *    their quantities and investment values
 * 4. **Normalizes asset types** (e.g., "mutual funds" → "fund", "stocks" → "stock")
 * 5. **Enriches holdings** with asset metadata including asset class and details
 *
 * @example
 *
 * ```ts
 * const enrichedPositions = await buildAccountsPositionsData(
 *   rawAccountsPositions,
 *   store.dispatch,
 * );
 *
 * enrichedPositions.forEach(account => {
 *   account.holdings.forEach(holding => {
 *     console.log(holding.ticker, holding.asset_class, holding.details?.logo);
 *   });
 * });
 * ```
 *
 * @param accountsPositions - Raw account positions from the brokerage API
 * @param dispatch - Redux dispatch function for fetching asset info
 * @returns Promise resolving to an array of {@link AccountsPositionsData} with
 *   merged holdings and enriched asset metadata
 */
export const buildAccountsPositionsData = async (
  accountsPositions: BrokerageTypes.GetAdvisoryAccountsPositionsResponseBody,
  institutions: Map<string, string>,
  dispatch: ThunkDispatch<any, any, any>,
): Promise<AccountsPositionsData[]> => {
  const tickers = new Set(
    accountsPositions
      .flatMap(account =>
        account.holdings.map(holding => holding.ticker ?? holding.isin),
      )
      .filter((ticker): ticker is string => Boolean(ticker)),
  );

  const { assets: assetsInfoResponse, classifierByTicker } =
    await getAssetsInfoBundle(Array.from(tickers), dispatch);

  const assetsInfo: Map<string, AssetInfo> = new Map(
    assetsInfoResponse.flatMap(asset => {
      const entries: [string, AssetInfo][] = [];
      if (asset.ticker) {
        entries.push([asset.ticker, asset]);
      }
      if (asset.isin) {
        entries.push([asset.isin, asset]);
      }
      return entries;
    }),
  );

  const mergedAccountsPositions = produce(accountsPositions, draft => {
    draft.forEach(account => {
      account.holdings = mergeClientHoldings(account.holdings);
    });
  });

  return mergedAccountsPositions.map(account => {
    return {
      institution_id: account.institution_id,
      institution_name: institutions.get(account.institution_id) ?? '',
      iban: account.iban,
      last_sync: account.last_sync,
      holdings: account.holdings.map(holding => {
        const assetInfo = assetsInfo.get(holding.ticker ?? holding.isin!);

        const lookupKey = holding.ticker ?? holding.isin!;

        const assetType = resolveHoldingDisplayAssetType({
          holdingAssetType: holding.asset_type,
          classifierAssetType: classifierByTicker.get(lookupKey),
          enrichedAssetType: assetInfo?.asset_type,
        });

        const assetClass = assetInfo ? assetInfo?.asset_class || 'Equity' : 'Other';

        return {
          ...holding,
          institution_name: institutions.get(account.institution_id) ?? '',
          ticker: assetInfo?.ticker ?? holding.ticker,
          isin: assetInfo?.isin ?? holding.isin,
          details: assetInfo,
          asset_type: assetType,
          asset_class: assetClass,
          weight: 0,
        };
      }),
    } as AccountsPositionsData;
  });
};
